/**
 * Project panel — dynamic table, drag-drop, selection, channels
 */
import { projectStore } from '../stores/projectStore';
import { selectionStore } from '../stores/selectionStore';
import { monitorStore } from '../stores/monitorStore';
import { templateRegistry } from '../engine/templateRegistry';
import { renderTemplate, createSyncedVideo } from '../engine/templateRenderer';
import { executeTakeItem } from '../engine/transitionEngine';
import { eventBus } from '../engine/eventBus';
import { getDataControllerLabel, openDataControllerMenu, closeDataControllerMenu } from './dataControllerMenu';

let tbodyEl: HTMLElement;
let tabsEl: HTMLElement;

export function initProject(): void {
  tbodyEl = document.getElementById('project-tbody')!;
  tabsEl = document.querySelector('.project-tabs')!;

  // "Add new item" button — opens add dialog (or adds from selected template)
  const addBtn = document.querySelector('.add-item-btn');
  addBtn?.addEventListener('click', addFromSelection);

  // Delete button
  const trashBtn = document.querySelector('.trash-btn');
  trashBtn?.addEventListener('click', deleteSelected);

  // Double-click on table row = preview
  tbodyEl.addEventListener('dblclick', (e) => {
    const row = (e.target as HTMLElement).closest('tr[data-item-id]') as HTMLElement;
    if (row) {
      const itemId = row.dataset.itemId!;
      monitorStore.getState().setPreview(itemId);
    }
  });

  // Subscribe to store changes
  projectStore.subscribe(renderTable);
  projectStore.subscribe(renderTabs);
  selectionStore.subscribe(renderTable);
  monitorStore.subscribe(renderTable);

  // Column resize handles
  initColumnResize();

  // Initial render
  renderTabs();
  renderTable();
}

function renderTabs(): void {
  const { channels, activeChannelIndex } = projectStore.getState();

  tabsEl.innerHTML = channels.map((ch) => `
    <div class="project-tab${ch.index === activeChannelIndex ? ' active' : ''}" data-channel="${ch.index}">
      ${ch.name}
    </div>
  `).join('') + `<div class="project-tab project-tab-add" title="Add Channel">+ Add Channel</div>` +
    `<span class="project-tabs-spacer"></span><button class="project-size-all-btn">Size All</button>`;

  // Tab click handlers
  tabsEl.querySelectorAll<HTMLDivElement>('.project-tab:not(.project-tab-add)').forEach((tab) => {
    tab.addEventListener('click', () => {
      projectStore.getState().setActiveChannel(Number(tab.dataset.channel));
    });

    // Double-click to rename
    tab.addEventListener('dblclick', () => {
      const idx = Number(tab.dataset.channel);
      const channel = projectStore.getState().channels.find((c) => c.index === idx);
      if (!channel) return;
      const input = document.createElement('input');
      input.type = 'text';
      input.value = channel.name;
      input.className = 'channel-rename-input';
      tab.textContent = '';
      tab.appendChild(input);
      input.focus();
      input.select();

      const commit = () => {
        const name = input.value.trim() || channel.name;
        projectStore.getState().renameChannel(idx, name);
      };
      input.addEventListener('blur', commit);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') input.blur();
        if (e.key === 'Escape') { input.value = channel.name; input.blur(); }
      });
    });
  });

  // Add channel button
  const addTab = tabsEl.querySelector('.project-tab-add');
  addTab?.addEventListener('click', () => {
    projectStore.getState().addChannel();
  });
}

function renderTable(): void {
  const items = projectStore.getState().getChannelItems();
  const selectedIds = selectionStore.getState().selectedProjectItemIds;
  const previewId = monitorStore.getState().previewItemId;

  if (items.length === 0) {
    tbodyEl.innerHTML = `<tr class="empty-row"><td colspan="6" style="text-align:center;color:var(--text-dim);padding:20px;">
      Drag templates from the Library, or click "Add new Item"
    </td></tr>`;
    return;
  }

  tbodyEl.innerHTML = items.map((item) => {
    const isSelected = selectedIds.includes(item.id);
    const isPreview = item.id === previewId;
    const isProgrammed = monitorStore.getState().programItemIds.includes(item.id);
    const isLiveCamera = item.mediaType === 'video' && item.name.startsWith('Decklink');

    return `<tr data-item-id="${item.id}" class="${isSelected ? 'selected' : ''} ${isPreview ? 'previewing' : ''}" draggable="true">
      <td class="col-name">${item.name}</td>
      <td class="col-preview"><div class="project-thumb" data-thumb-id="${item.id}"></div></td>
      <td class="col-data"><span class="dc-cell" data-dc-id="${item.id}">${getDataControllerLabel(item.id) || '<span class="dc-placeholder">Add Source</span>'}</span></td>
      <td class="col-hotkey">${item.hotKey || ''}</td>
      <td class="col-duration">${isLiveCamera ? '<span style="color:var(--text-dim);">-- -- --</span>' : (item.duration === 0 ? 'Manual' : (item.duration / 1000).toFixed(1) + 's')}</td>
      <td class="col-play">${isLiveCamera ? '<img src="/assets/icons/play-switcher-red_64x64.png" style="width:18px;height:18px;object-fit:contain;" alt="Live">' : `<button class="project-play-btn${isProgrammed ? ' active' : ''}" data-play-id="${item.id}" title="Send to Program"><img class="play-icon-off" src="/assets/icons/play-title-grey.png" alt=""><img class="play-icon-on" src="/assets/icons/play-title-red.png" alt=""></button>`}</td>
    </tr>`;
  }).join('');

  // Render thumbnails into inner wrappers so .template-viewport doesn't
  // override .project-thumb's explicit 120x34 sizing.
  tbodyEl.querySelectorAll<HTMLElement>('.project-thumb').forEach((thumb) => {
    const itemId = thumb.dataset.thumbId!;
    const item = projectStore.getState().getItem(itemId);
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:absolute;inset:0;';
    thumb.appendChild(wrapper);
    // Live camera items show synced video preview in thumbnail
    if (item && item.mediaType === 'video' && item.name.startsWith('Decklink') && item.mediaUrl) {
      const canvas = createSyncedVideo(item.mediaUrl);
      canvas.style.objectFit = 'cover';
      wrapper.appendChild(canvas);
    } else {
      renderTemplate(wrapper, itemId, true);
    }
  });

  // Row click handlers
  tbodyEl.querySelectorAll<HTMLTableRowElement>('tr[data-item-id]').forEach((row) => {
    const itemId = row.dataset.itemId!;

    row.addEventListener('click', (e) => {
      // Ignore clicks on play button or data controller cell
      if ((e.target as HTMLElement).closest('.project-play-btn')) return;
      if ((e.target as HTMLElement).closest('.dc-cell')) return;

      if (e.ctrlKey || e.metaKey) {
        selectionStore.getState().toggleProjectItem(itemId);
      } else if (e.shiftKey) {
        handleShiftClick(itemId);
      } else {
        selectionStore.getState().select({ type: 'projectItem', itemId });
        monitorStore.getState().setPreview(itemId);
      }
    });

    // Drag from project table
    row.addEventListener('dragstart', (e) => {
      e.dataTransfer!.setData('application/x-project-item-id', itemId);
      e.dataTransfer!.effectAllowed = 'move';
    });
  });

  // Play button click handlers
  tbodyEl.querySelectorAll<HTMLButtonElement>('.project-play-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const itemId = btn.dataset.playId!;
      executeTakeItem(itemId);
    });
  });

  // Data Controller cell click handlers
  tbodyEl.querySelectorAll<HTMLElement>('.dc-cell').forEach((cell) => {
    cell.addEventListener('click', (e) => {
      e.stopPropagation();
      const itemId = cell.dataset.dcId!;
      openDataControllerMenu(itemId, cell);
    });
  });
}

/** Public re-render (used by dataControllerMenu after connecting) */
export function renderProjectList(): void {
  renderTable();
}

function handleShiftClick(itemId: string): void {
  const items = projectStore.getState().getChannelItems();
  const selectedIds = selectionStore.getState().selectedProjectItemIds;
  const itemIds = items.map((i) => i.id);

  const lastSelected = selectedIds[selectedIds.length - 1];
  const lastIdx = lastSelected ? itemIds.indexOf(lastSelected) : 0;
  const clickIdx = itemIds.indexOf(itemId);

  const start = Math.min(lastIdx, clickIdx);
  const end = Math.max(lastIdx, clickIdx);
  const range = itemIds.slice(start, end + 1);
  selectionStore.getState().selectProjectItems(range);
}

function addFromSelection(): void {
  const sel = selectionStore.getState().current;
  if (sel.type === 'template') {
    const template = templateRegistry.getTemplate(sel.templateId);
    if (template) {
      const item = projectStore.getState().addItem({
        templateId: sel.templateId,
        name: template.name,
        fieldValues: templateRegistry.getDefaultFieldValues(sel.templateId),
        duration: template.duration ?? 0,
        isActive: false,
      });
      monitorStore.getState().setPreview(item.id);
    }
  }
}

function deleteSelected(): void {
  const ids = selectionStore.getState().selectedProjectItemIds;
  if (ids.length > 0) {
    projectStore.getState().removeItems(ids);
    selectionStore.getState().clearSelection();
  }
}

function initColumnResize(): void {
  const table = document.querySelector('.project-table') as HTMLTableElement;
  if (!table) return;

  table.querySelectorAll<HTMLElement>('.th-resize-handle').forEach((handle) => {
    const th = handle.parentElement as HTMLTableCellElement;
    let startX = 0;
    let startW = 0;

    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      startX = e.clientX;
      startW = th.offsetWidth;

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });

    function onMove(e: MouseEvent): void {
      const newW = Math.max(40, startW + (e.clientX - startX));
      th.style.width = `${newW}px`;
    }

    function onUp(): void {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    }
  });
}
